import {
  AbsoluteCenter,
  Box,
  chakra,
  Checkbox,
  Flex,
  IconButton,
  Image,
  shouldForwardProp,
  Text,
  Spinner,
} from "@chakra-ui/react";
import React, { useEffect, useState, useRef } from "react";
import { COMMON_WEB_PATH } from "../../constant";
import { useDispatch, useSelector } from "react-redux";
import { toggleBGM } from "../../redux/action/action";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isValidMotionProp, motion } from "framer-motion";
import ClearCashButTransfer from "../ClearCash/ClearCashButTransfer";
import i18next from "i18next";
import html2canvas from "html2canvas";
import { QRCodeCanvas } from "qrcode.react";
import { appName } from "../../config";

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const EndGameDetail = ({
  onClose,
  previousBalance,
  transCode,
  withdrawalNumber,
}) => {
  const { t } = useTranslation();
  const i18n = (key) => t(`common.${key}`);

  const captureRef = useRef(null);
  const [snapshotUrl, setSnapshotUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(true);

  const [isClosing, setIsClosing] = useState(false);
  const [qrcodeModal, setQrcodeModal] = useState(false);
  const [alreadyScan, setAlreadyScan] = useState(false);

  const memberInfo = useSelector((state) => state.isMemberInfo);
  const [currentTime] = useState(dayjs().format("YYYY-MM-DD HH:mm:ss"));
  const navigate = useNavigate();

  const isBGMEnabled = useSelector((state) => state.isBGMEnabled);
  const dispatch = useDispatch();

  useEffect(() => {
    const generateSnapshot = async () => {
      if (captureRef.current) {
        try {
          // 延遲以確保所有樣式與圖片加載完成
          await new Promise((resolve) => setTimeout(resolve, 800));

          const canvas = await html2canvas(captureRef.current, {
            scale: 2,
            useCORS: true,
            backgroundColor: null,
            onclone: (clonedDoc) => {
              // 修正 html2canvas 不支援 background-clip: text 的問題
              const balanceText = clonedDoc.querySelector(".snapshot-balance");
              if (balanceText) {
                balanceText.style.background = "none";
                balanceText.style.webkitTextFillColor = "#fdd26a";
                balanceText.style.color = "#fdd26a";
              }
            },
          });

          setSnapshotUrl(canvas.toDataURL("image/png"));
        } catch (error) {
          console.error("Failed to generate snapshot:", error);
        } finally {
          setIsGenerating(false);
        }
      }
    };

    generateSnapshot();
  }, [previousBalance, currentTime, withdrawalNumber]);

  const handleConfirm = () => {
    setIsClosing(true);
    dispatch(
      toggleBGM({
        ...isBGMEnabled,
        song: "back2_",
        musicVolume: 0.2,
      }),
    );
    navigate("/");
    onClose();
  };

  const qrUrl = `${window.location.origin}/cash-register/order/${withdrawalNumber}`;

  const GameInfoContent = ({ isSnapshot = false }) => (
    <>
      <AbsoluteCenter
        display="flex"
        fontSize="24px"
        color="white"
        textAlign="center"
        mt={4}
        bgPosition="center"
        bgRepeat="no-repeat"
        bgSize="100% 100%"
        sx={{ width: "-webkit-fill-available" }}
        justifyContent="center"
        alignItems="center"
        top="-20px"
      >
        <Image
          src={`${COMMON_WEB_PATH}/now888/modal/give_up_title${
            i18next.language == "tw" ? "" : `_${i18next.language}`
          }.png`}
          height={{
            base: "50px",
            xl: "60px",
          }}
        />
      </AbsoluteCenter>

      <Flex
        flexDirection="column"
        gap={2}
        justifyContent="center"
        position="relative"
        zIndex={2}
        w="100%"
        pt="25px"
      >
        <Flex
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          mb="25px"
        >
          <Flex
            justifyContent="center"
            alignItems="center"
            fontSize="36px"
            color="white"
            textAlign="center"
            gap={4}
          >
            {i18n("machineCode")}
            <Text color="#fff" fontWeight="700">
              {"<NO."}
              {memberInfo?.uid || "-"}
              {">"}
            </Text>
          </Flex>
          <Box fontSize="20px" textAlign="center" mt={1}>
            <Text color="#C5B3C7" fontWeight="500">
              {currentTime}
            </Text>
          </Box>
          <Box fontSize="20px" textAlign="center">
            <Flex gap={2} color="#C5B3C7" fontWeight="500">
              <span>{i18n("withdrawNumber")}:</span>
              {withdrawalNumber || "-"}
            </Flex>
          </Box>
        </Flex>

        <Box fontSize="32px" textAlign="center" mt={1}>
          <Text color="#fdd26a" fontWeight="500">
            {i18n("GAME RESULT")}
          </Text>
        </Box>

        <Box position="relative" textAlign="center">
          <Text
            className="snapshot-balance"
            fontWeight="700"
            fontSize="36px"
            sx={{
              background:
                "linear-gradient(180deg, #fff 0%,#fdd26a 60%,  #b07f20 80%,  #b07f20 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: isSnapshot ? "#fdd26a" : "transparent",
              textShadow: "0px 1px 3px rgba(255, 215, 0, 0.4)",
            }}
          >
            {previousBalance || 0}
          </Text>

          {/* 浮水印 */}
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%) rotate(-5deg)"
            opacity={0.05}
            pointerEvents="none"
            whiteSpace="nowrap"
            fontSize="32px"
            fontWeight="bold"
            fontStyle="italic"
            color="white"
            zIndex={1}
          >
            {appName}-{i18n("棄分")}:{previousBalance}
          </Box>
        </Box>
      </Flex>
    </>
  );

  return (
    <Flex
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      bg="rgba(0, 0, 0, 0.8)"
      zIndex="9999"
      backdropFilter="blur(2px)"
      justifyContent="center"
      alignItems="center"
    >
      {/* 1. Snapshot Area (Hidden) */}
      <Box
        ref={captureRef}
        position="absolute"
        left="-9999px"
        w="520px"
        bgImage={`url('${COMMON_WEB_PATH}/now888/modal/hintBG.webp')`}
        bgPosition="center"
        bgRepeat="no-repeat"
        bgSize="100% 100%"
        p={6}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <GameInfoContent isSnapshot={true} />
      </Box>

      {/* 2. Display Layer */}
      <ChakraBox
        as={Flex}
        w="520px"
        position={"relative"}
        sx={{
          bgImage: `url('${COMMON_WEB_PATH}/now888/modal/hintBG.webp')`,
          bgPosition: "center",
          bgRepeat: "no-repeat",
          bgSize: "100% 100%",
        }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        p={6}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        gap={4}
      >
        {isGenerating ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            h="400px"
            w="100%"
          >
            <Spinner size="xl" color="brand.500" mb={4} />
            <Text color="white">{i18n("generatingSnapshot")}</Text>
          </Flex>
        ) : (
          <>
            <Image src={snapshotUrl} alt="verification" w="100%" />

            {/* QRCode Area */}
            <Flex direction="column" align="center" gap={2} mt="-20px">
              <Box bg="white" p={2} borderRadius="md" boxShadow="lg">
                <QRCodeCanvas value={qrUrl} size={90} />
              </Box>
              <Text fontSize="xs" color="gray.400">
                {i18n("orderVerification")}
              </Text>
            </Flex>

            {/* Confirm Button */}
            <Flex
              as="button"
              w="165px"
              h="62px"
              bgImage={`url('${COMMON_WEB_PATH}/now888/button/confirm_background.png')`}
              bgPosition="center"
              bgRepeat="no-repeat"
              bgSize="100% 100%"
              cursor="pointer"
              justifyContent="center"
              alignItems="center"
              onClick={handleConfirm}
              _hover={{ filter: "brightness(1.1)" }}
              zIndex={3}
              mt={2}
            >
              <Image
                src={`${COMMON_WEB_PATH}/now888/button${
                  i18next.language == "tw" ? "" : `/${i18next.language}`
                }/confirm_text.png`}
                width="70px"
                height="30px"
              />
            </Flex>
          </>
        )}

        {/* QRcode遮罩 */}
        <ClearCashButTransfer
          onCancelClearCash={onClose}
          transCode={transCode}
        />
      </ChakraBox>
    </Flex>
  );
};

export default EndGameDetail;
